import { useEffect } from "react"
import CwDataTable from "../../components/construction/CwDataTable"
import CwPageHeader from "../../components/construction/CwPageHeader"
import SignInLocationCell from "../../components/construction/SignInLocationCell"
import { t } from "../../i18n"
import { CwAdminStaffEmployee, CwAdminTask, CwAdminWorkDay } from "../../Interfaces"
import { detailTaskLabel, sortTasks, workDayHours } from "../../utils/adminTimeSheet"
import { formatHours } from "../../utils/jobSiteDailyHours"
import { punchDistanceLabel, punchKindLabel } from "../../utils/punchLocations"

const tasks: CwAdminTask[] = sortTasks([
  { id: 1, managementCompanyId: 12, nameEn: "Reception", nameEs: "Recepción", payrollCode: "9010", sortOrder: 10, active: true },
  { id: 2, managementCompanyId: 12, nameEn: "Office work", nameEs: "Trabajo de oficina", payrollCode: "9020", sortOrder: 20, active: true },
])

const staff: CwAdminStaffEmployee[] = [
  { id: 701, firstName: "Ada", lastName: "Lovelace", employeeNum: 42, managementCompanyId: 12, userId: 9, adminModeEnabled: true },
  { id: 702, firstName: "Alan", lastName: "Turing", employeeNum: 43, managementCompanyId: 12, userId: null, adminModeEnabled: false },
]

const days: CwAdminWorkDay[] = [
  {
    id: 80,
    date: "2026-10-22",
    mode: "ADMIN",
    clockedInAt: "2026-10-22T15:00:00.000Z",
    clockedOutAt: "2026-10-22T23:00:00.000Z",
    employee: { id: 701, firstName: "Ada", lastName: "Lovelace", employeeNum: 42 },
    details: [
      { id: 1, adminTaskId: 1, adminTask: { id: 1, nameEn: "Reception", nameEs: "Recepción" }, startedAt: "2026-10-22T15:00:00.000Z", endedAt: "2026-10-22T18:00:00.000Z", hours: 3 },
      { id: 2, customText: "Bank deposit", startedAt: "2026-10-22T18:00:00.000Z", endedAt: "2026-10-22T23:00:00.000Z", hours: 5 },
    ],
    clockInLatitude: 37.639,
    clockInLongitude: -120.997,
    clockInAccuracyM: 12,
    clockInLocationStatus: "granted",
    punchLocations: [
      {
        id: 2,
        workDayId: 80,
        kind: "TASK_CHANGE",
        status: "granted",
        latitude: 37.64,
        longitude: -120.99,
        accuracyM: 8,
        capturedAt: "2026-10-22T18:00:00.000Z",
        referenceKind: "office",
        distanceMiles: 0.12,
        thresholdMiles: 0.5,
        flaggedFar: false,
        reviewedAt: null,
        reviewedByUserId: null,
        createdAt: "2026-10-22T18:00:00.000Z",
      },
      {
        id: 3,
        workDayId: 80,
        kind: "CLOCK_OUT",
        status: "granted",
        latitude: 37.7,
        longitude: -121.1,
        accuracyM: 20,
        capturedAt: "2026-10-22T23:00:00.000Z",
        referenceKind: "office",
        distanceMiles: 6.4,
        thresholdMiles: 0.5,
        flaggedFar: true,
        reviewedAt: null,
        reviewedByUserId: null,
        createdAt: "2026-10-22T23:00:00.000Z",
      },
    ],
  },
]

function formatTime(value: string | null | undefined): string {
  if (!value) return ""
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
}

/**
 * Unauthenticated fixture for ConstructionWalk Admin mode.
 * /preview/admin-mode?product=construction
 */
export default function AdminModePreview() {
  useEffect(() => {
    document.documentElement.classList.add("construction-walk")
  }, [])

  return (
    <div className="min-h-screen p-4 md:p-8 bg-secondary-extra-light">
      <CwPageHeader title={t("adminMode.title")} subtitle={`Valley Builders · VAL · ${t("adminMode.subtitle")}`} />

      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-1">{t("adminMode.tasksTitle")}</h2>
        <p className="text-sm text-gray-600 font-source mb-3">{t("adminMode.tasksHelper")}</p>
        <CwDataTable<CwAdminTask>
          columns={[
            { key: "nameEn", label: t("adminMode.columnTask") },
            { key: "nameEs", label: t("adminMode.columnSpanish"), render: (row) => row.nameEs ?? "" },
            { key: "payrollCode", label: t("adminMode.columnPayrollCode"), render: (row) => row.payrollCode ?? "" },
            { key: "active", label: t("adminMode.columnStatus"), render: (row) => (row.active ? t("adminMode.active") : t("adminMode.inactive")) },
          ]}
          rows={tasks}
          empty={t("adminMode.tasksEmpty")}
        />
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-1">{t("adminMode.staffTitle")}</h2>
        <p className="text-sm text-gray-600 font-source mb-3">{t("adminMode.staffHelper")}</p>
        <CwDataTable<CwAdminStaffEmployee>
          columns={[
            { key: "name", label: t("adminMode.columnEmployee"), render: (row) => `${row.firstName} ${row.lastName}` },
            { key: "adminModeEnabled", label: t("adminMode.columnAdminMode"), render: (row) => (row.adminModeEnabled ? t("adminMode.adminModeOn") : t("adminMode.adminModeOff")) },
            { key: "login", label: t("adminMode.columnLogin"), render: (row) => (row.userId ? "ada@example.com" : t("adminMode.notLinked")) },
          ]}
          rows={staff}
          empty={t("adminMode.staffEmpty")}
        />
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">{t("adminMode.timeSheetTitle")}</h2>
        <CwDataTable<CwAdminWorkDay>
          columns={[
            { key: "date", label: t("adminMode.columnDate"), render: (row) => String(row.date).slice(0, 10) },
            { key: "employee", label: t("adminMode.columnEmployee"), render: (row) => [row.employee?.firstName, row.employee?.lastName].filter(Boolean).join(" ") },
            { key: "in", label: t("adminMode.columnClockIn"), render: (row) => formatTime(row.clockedInAt) },
            { key: "out", label: t("adminMode.columnClockOut"), render: (row) => formatTime(row.clockedOutAt) },
            {
              key: "tasks",
              label: t("adminMode.columnTasks"),
              render: (row) => (
                <ul className="list-none m-0 p-0">
                  {(row.details ?? []).map((d) => (
                    <li key={d.id}>{detailTaskLabel(d, "en", t("adminMode.custom"))}</li>
                  ))}
                </ul>
              ),
            },
            { key: "hours", label: t("adminMode.columnHours"), render: (row) => formatHours(workDayHours(row, 0)) },
            { key: "location", label: t("adminMode.columnLocation"), render: (row) => <SignInLocationCell source={row} /> },
            {
              key: "punches",
              label: t("adminMode.columnPunches"),
              render: (row) => (
                <ul className="list-none m-0 p-0">
                  {(row.punchLocations ?? []).filter((p) => p.kind !== "CLOCK_IN").map((p) => (
                    <li key={p.id} className={p.flaggedFar ? "text-amber-700" : undefined}>
                      {punchKindLabel(p.kind)}
                      {p.flaggedFar ? ` · ${t("timeClock.farFlag")} (${punchDistanceLabel(p)})` : null}
                    </li>
                  ))}
                </ul>
              ),
            },
          ]}
          rows={days}
          empty={t("adminMode.timeSheetEmpty")}
        />
      </section>
    </div>
  )
}
